/**
 * front/src/pages/SignUp.tsx
 * (Adds bottom‑of‑card language switcher)
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  GlobalStyles,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../redux/hooks';
import Logo from '../components/general/navBar/Logo';
import { signUpDoctor } from '../routes/general/authentication';
import LanguageIcon from '@mui/icons-material/Language';

/* ---------- styled helpers (unchanged) ---------- */
const Card = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 400,
  padding: theme.spacing(4, 5), // Reduced vertical padding
  borderRadius: 16,
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const Field = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: '#fff',
    '& input': { color: '#1a1a1a', '::placeholder': { color: '#9e9e9e', opacity: 1 } },
    '& fieldset': { borderColor: '#d0d0d0' },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 2 },
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 24,
  height: 46,
  boxShadow: theme.shadows[2],
  '&:hover': { boxShadow: theme.shadows[4] },
  '&:focus-visible': { outline: `3px solid ${theme.palette.primary.light}`, outlineOffset: 2 },
}));

/* ---------- component ---------- */
const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const colors = useAppSelector((s) => s.theme.colors);

  const { referralLinkId } = useParams(); 

  const bg         = colors?.background    || '#f4f5f7';
  const textMain   = colors?.text          || '#222';
  const textGrey   = colors?.textSecondary || '#5f6368';
  const primaryClr = colors?.primary       || '#0B69FF';

  /* form state */
  const [first, setFirst] = useState('');
  const [last,  setLast]  = useState('');
  const [email, setEmail] = useState('');
  const [ok,    setOk]    = useState(false);
  const [busy,  setBusy]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [err,   setErr]   = useState<{ [k: string]: string }>({});
  const [apiErr, setApiErr] = useState<string | null>(null);

  const emailRx = /\S+@\S+\.\S+/;

  const validate = () => {
    const e: { [k: string]: string } = {};
    if (!first.trim()) e.first = t('errorFirstNameRequired');
    if (!last.trim())  e.last  = t('errorLastNameRequired');
    if (!email.trim()) e.email = t('errorEmailRequired');
    else if (!emailRx.test(email)) e.email = t('errorEmailInvalid');
    if (!ok)           e.terms = t('errorTermsRequired');
    setErr(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    setBusy(true);
    setApiErr(null);

    try {

      const config = { 
        firstName: first,
        lastName: last, 
        email, 
        referralLinkId 
      }; 

      const res = await signUpDoctor(config);

      if (res?.data.status === 200 || res?.data.status === 201) {
        setSuccess(true);
        setBusy(false);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setApiErr(res?.data?.message || t('signupFailed'));
        setBusy(false);
      }
    } catch {
      setApiErr(t('signupFailed'));
      setBusy(false);
    }
  };

  /* ---------- render ---------- */
  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: bg } }} />

      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <Card elevation={3}>
          {/* Brand */}
          <Logo size={40} />

          <Typography variant="h6" sx={{ mt: 2, fontWeight: 600, color: textMain, textAlign: 'center' }}>
            {t('signupHeadline')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: textGrey, textAlign: 'center' }}>
            {t('trialOfferShort')}
          </Typography>

          {/* Form */}
          <Box component="form" onSubmit={submit} sx={{ width: '100%' }}>
            <Stack spacing={2}>
              <Field
                placeholder={String(t('firstName'))}
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                error={!!err.first}
                helperText={err.first}
                disabled={busy}
                fullWidth
              />
              <Field
                placeholder={String(t('lastName'))}
                value={last}
                onChange={(e) => setLast(e.target.value)}
                error={!!err.last}
                helperText={err.last}
                disabled={busy}
                fullWidth
              />
              <Field
                placeholder={String(t('email'))}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!err.email}
                helperText={err.email}
                disabled={busy}
                fullWidth
              />

              <FormControlLabel
                sx={{ alignItems: 'center', mt: 0.5 }}
                control={
                  <Checkbox
                    checked={ok}
                    onChange={(e) => setOk(e.target.checked)}
                    disabled={busy}
                    sx={{ p: 0.5, color: primaryClr, '&.Mui-checked': { color: primaryClr } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: err.terms ? 'error.main' : textGrey }}>
                    {t('iAgreeTo')} 
                    <Link href="/legal/tos" target="_blank" rel="noopener" sx={{ color: primaryClr }}>
                      {t('termsOfService')}
                    </Link>
                     {t('and')} 
                    <Link href="/legal/privacy" target="_blank" rel="noopener" sx={{ color: primaryClr }}>
                      {t('privacyPolicy')}
                    </Link>
                  </Typography>
                }
              />

              <PrimaryButton
                variant="contained"
                fullWidth
                disabled={busy}
                sx={{ backgroundColor: primaryClr, mt: 1 }}
                type="submit"
              >
                {busy && <CircularProgress size={22} sx={{ position: 'absolute', color: '#fff' }} />}
                <Box component="span" sx={{ visibility: busy ? 'hidden' : 'visible' }}>
                  {t('startFreeTrial')}
                </Box>
              </PrimaryButton>

              {apiErr && (
                <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                  {apiErr}
                </Typography>
              )}
            </Stack>
          </Box>

          <Typography variant="body2" sx={{ mt: 2.5, color: textGrey }}>
            {t('alreadyHaveAccount')} 
            <Link component="button" onClick={() => navigate('/login')} sx={{ fontWeight: 500, color: primaryClr }}>
              {t('loginButton')}
            </Link>
          </Typography>

          <Typography variant="caption" sx={{ mt: 2, color: textGrey }}>
            {t('compliant')}
          </Typography>

          <Button
            startIcon={<LanguageIcon fontSize="small" />}
            size="small"
            variant="text"
            onClick={() =>
            i18n.changeLanguage(i18n.language.startsWith('en') ? 'fr-CA' : 'en')
            }
            sx={{
                mt: 2,
                px: 1,
                minHeight: 32,
                color: textGrey,
                textTransform: 'none',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                  color: textGrey,
                },
                '&:focus': {
                  outline: 'none',
                  backgroundColor: 'transparent',
                  color: textGrey,
                },
                '& .MuiTouchRipple-root': { display: 'none' } // kill ripple if needed
              }}
          >
            {i18n.language.startsWith('en') ? 'Français' : 'English'}
          </Button>
        </Card>
      </Box>

      {/* success toast */}
      <Snackbar open={success} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" sx={{ width: '100%' }}>
          {t('signupSuccess')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignUp;